import { Button, Checkbox, Container, Input, Typography } from '@app/components';
import { Divider } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { QRCodeFormSchema } from '../../api/qr-code.form.schema.api';
import { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useUpdateQRCode } from '../../api/qr-code.hooks.api';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Dialog } from '@app/hoc';
import { clsxm } from '@app/styles/clsxm';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';

export const QRSettingsCurrentTab: React.FC<{
  mutationForUpdate: ReturnType<typeof useUpdateQRCode>;
}> = ({ mutationForUpdate }) => {
  const { t } = useTranslation();
  const { watch, setValue, getValues } = useFormContext<QRCodeFormSchema>();
  const { mutate: updateQRCode, isLoading: isLoadingForUpdate } = mutationForUpdate;
  const { show: openDialog, visible } = useModal(ConfirmationPasswordDialog);

  const passwordOld = watch('password');

  const [enablePassword, setEnablePassword] = useState(Boolean(passwordOld));
  const [passwordNew, setPasswordNew] = useState<string>(passwordOld || '');

  useEffect(() => {
    setEnablePassword(Boolean(passwordOld));
  }, [passwordOld]);

  const updatePasswordHandler = (newPassword: string | null) => {
    const values = getValues();
    if (!values.id) {
      // id not defined during QR code creation
      setValue('password', newPassword || undefined);
      setPasswordNew(newPassword || '');
      return;
    }
    updateQRCode(
      {
        id: values.id,
        password: newPassword,
      },
      {
        onSuccess: () => {
          setValue('password', newPassword);
          toast.success(t('toaster.qrPasswordUpdated'));
          setPasswordNew(newPassword || '');
        },
      }
    );
  };

  const changePasswordEnabledHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (enablePassword && !visible && passwordOld) {
      openDialog({ onConfirm: () => updatePasswordHandler(null) });
      return;
    }
    setEnablePassword(e.target.checked);
  };

  return (
    <Container className="flex flex-col gap-4 xl:rounded-xl">
      <div className="flex flex-col gap-4">
        <Typography className="font-bold sm:text-l">{t('qr.settings.current.title')}</Typography>
        <Typography className="font-normal sm:text-s">{t('qr.settings.current.description')}</Typography>
      </div>
      <Divider className="my-2 md:col-span-2" />

      <div className="flex flex-col gap-4">
        <Typography className="font-bold sm:text-l">{t('qr.settings.current.enablePasswordLabel')}</Typography>
        <Checkbox
          label={<span className="text-gray-700">{t('qr.settings.current.passwordCkeckboxLabel')}</span>}
          checked={enablePassword}
          onChange={changePasswordEnabledHandler}
        />
      </div>
      {enablePassword ? (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <Typography className="font-bold sm:text-l">{t('qr.settings.current.inputPasswordLabel')}</Typography>
            <Input
              value={passwordNew || ''}
              label={t('qr.settings.current.inputPasswordHeader')}
              withBG
              placeholder={t('qr.settings.current.inputPasswordHeader')}
              onChange={(e) => setPasswordNew(e.target.value.trim())}
            />
            <ConfirmationPasswordDialog
              id="confirmation-password-dialog"
              onConfirm={() => updatePasswordHandler(null)}
            />
          </div>
          <div>
            {!passwordOld && (
              <Button
                onClick={() => updatePasswordHandler(passwordNew)}
                loading={isLoadingForUpdate}
                disabled={!passwordNew}
                className="px-10"
              >
                {t('qr.settings.current.setPasswordButton')}
              </Button>
            )}
            {passwordOld && (
              <Button
                disabled={passwordOld === passwordNew || !passwordNew}
                onClick={() => updatePasswordHandler(passwordNew)}
                variant="outlined"
                loading={isLoadingForUpdate}
                className="px-10"
              >
                Change Password
              </Button>
            )}
          </div>
        </div>
      ) : (
        isLoadingForUpdate && <ContentLoader loading />
      )}
    </Container>
  );
};

export const ConfirmationPasswordDialog = NiceModal.create(({ onConfirm }: { onConfirm?: () => void }) => {
  const isMobile = useIsMobile();
  const { remove: removeDialog, visible } = useModal();
  const { t } = useTranslation();

  const handlerConfirm = () => {
    onConfirm?.();
    removeDialog();
  };
  return (
    <Dialog
      open={visible}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          // ok to close on backdrop
          // return;
        }
        removeDialog();
      }}
      hideHeader
      paperClassName="text-accept-dark-blue shadow-none bg-transparent rounded-none"
      maxWidth="md"
      fullScreen={isMobile}
    >
      <div className="flex flex-col gap-4 pt-4 md:pt-0">
        <div
          className={clsxm(
            'rounded-2xl bg-white shadow overflow-hidden',
            // extra space needed on mobile to avoid overlap with support chat FAB on prod
            'pb-20 md:pb-0'
          )}
        >
          <div className="flex flex-col gap-6 p-8 max-w-[504px]">
            <div className="flex flex-col gap-4">
              <h2 className="text-xl text-gray-800 font-bold">{t('qr.settings.current.confirmationDialog.title')}</h2>
              <p className="text-s text-gray-700">{t('qr.settings.current.confirmationDialog.description')}</p>
              <span className="text-s text-gray-700 italic">{t('qr.settings.current.confirmationDialog.message')}</span>
            </div>
            <div className="flex gap-4 w-full">
              <Button className="flex-1" variant="outlined" onClick={removeDialog}>
                {t('qr.settings.current.confirmationDialog.buttonCancel')}
              </Button>
              <Button className="flex-1" onClick={handlerConfirm}>
                {t('qr.settings.current.confirmationDialog.buttonConfirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
});
